/**
 * "Gerenciar plano" (spec 037): a equipe corrige o plano, a etapa, a conta e a plataforma num lugar so, a qualquer
 * momento, como o "Gerenciar conta" do legado. A tela so mostra o que esta salvo, marca o que a pessoa mudou e manda
 * SO o que mudou (`PATCH .../gestao`); quem decide se pode e o backend, e a recusa aparece como ele escreveu.
 * Mexeu na conta ou na etapa, confere antes de gravar, como na liberacao (Vitor, 01/10: "na troca repentina tambem deve
 * perguntar, pra confirmar, por mais que possa permanecer a mesma"). Subconta com outro plano em andamento na mesma mesa:
 * o backend pergunta (409 `CONFIRMAR_CONTA_EM_USO`) e a conferencia mostra o alerta com "Confirmar mesmo assim".
 */
import { Aviso, BlocoDeFormulario, Botao, CampoData, CampoMudado, CampoSelecao, CampoTextoLongo, Campo, Carregando, Dialogo, ErroApi, ListaDeEscolha, Segmentado, formatarDia, hojeEmSaoPaulo, soDigitos } from "@mtc/ui";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { ETAPA, STATUS_DA_MATRICULA, FAMILIA, ORIGENS_DA_MUDANCA, erroDe, useAcao, useApi, type Etapa, type FamiliaDoPlano, type GestaoDaMatricula, type MudancaDaGestao, type Plataforma } from "../api";
import { ConfirmarConta, parDaConta, useContaEmUso, type CorpoDaConfirmacao } from "./DeclararConta";
import { FormularioDaGlobal } from "./GerenciarPlanoDaGlobal";

type Formulario = {
  plano: string;
  etapa: Etapa | "";
  /** Inicio da etapa escolhida, quando a etapa muda. */
  dia: string;
  inicios: Partial<Record<Etapa, string>>;
  mesa: string;
  subconta: string;
  plataforma: Plataforma | "";
  motivo: string;
  origem: string;
};

/**
 * A primeira mensalidade, quando a correcao faz o plano PASSAR a pagar (spec 015, decisao 6). Quem sabe se passa a
 * pagar e o backend: a tela so abre o bloco quando ele recusa com `DECIDIR_MENSALIDADE`, e nenhuma escolha vem marcada.
 */
type DecisaoDaMensalidade = "JA_PAGOU" | "COBRAR" | "SEM_COBRANCA";
type Mensalidade = { decisao: DecisaoDaMensalidade | ""; como: string; dia: string; vencimento: string; motivo: string };
const PEDE_DECISAO = "DECIDIR_MENSALIDADE";

function doSalvo(g: GestaoDaMatricula): Formulario {
  const e = g.modulos.etapas;
  return {
    plano: g.plano.slug,
    etapa: e?.etapa?.codigo ?? "",
    dia: hojeEmSaoPaulo(),
    inicios: Object.fromEntries((e?.inicios ?? []).map((x) => [x.etapa, x.dia ?? ""])),
    mesa: e?.conta.mesa ?? "",
    subconta: e?.conta.subconta ?? "",
    plataforma: e?.plataforma ?? "",
    motivo: "",
    origem: "CONFERENCIA_INTERNA",
  };
}

const dia = (d: string | null | undefined) => (d ? formatarDia(d) : "sem data");
const plural = (n: number) => (n === 1 ? "1 mudança" : `${n} mudanças`);

export function GerenciarPlano({ matriculaId, aberto, aoFechar, aoSalvar }: { matriculaId: string; aberto: boolean; aoFechar: () => void; aoSalvar: (r: { mudancas: MudancaDaGestao[] }) => void }) {
  const { ler } = useApi();
  // Sempre o estado de agora: outra aba ou outro administrador pode ter mudado o plano desde a ultima abertura.
  const q = useQuery({ queryKey: ["gestao", matriculaId], queryFn: () => ler<GestaoDaMatricula>(`/v1/admin/matriculas/${matriculaId}/gestao`), enabled: aberto, staleTime: 0, gcTime: 0 });
  const pronto = aberto && q.data && !q.isFetching;
  return (
    <Dialogo aberto={aberto} aoFechar={aoFechar} titulo="Gerenciar plano" descricao="Corrija o que estiver errado. Só o que você mudar é salvo, com o motivo, e fica na trilha com o seu nome.">
      {!pronto ? (
        q.isError ? (
          <>
            <Aviso tipo="erro">{erroDe(q.error)}</Aviso>
            <div className="mtc-dialogo__rodape"><Botao variante="fantasma" onClick={aoFechar}>Fechar</Botao></div>
          </>
        ) : <Carregando compacto texto="Lendo o plano" />
      ) : q.data.encerrada ? (
        <>
          <Aviso tipo="alerta">Este plano está encerrado. Para corrigir, desfaça o encerramento antes, pelo botão <strong>Desfazer encerramento</strong> da ficha.</Aviso>
          <div className="mtc-dialogo__rodape"><Botao onClick={aoFechar}>Entendi</Botao></div>
        </>
      ) : q.data.modulos.corretora ? (
        // Global (spec 044): plano, etapa com o dia e conta. Acende pelo gestor do modulo `nelogica-v2`, nunca pela marca.
        <FormularioDaGlobal g={q.data} c={q.data.modulos.corretora} matriculaId={matriculaId} aoFechar={aoFechar} aoSalvar={aoSalvar} />
      ) : (
        <FormularioDeGestao g={q.data} matriculaId={matriculaId} aoFechar={aoFechar} aoSalvar={aoSalvar} />
      )}
    </Dialogo>
  );
}

function FormularioDeGestao({ g, matriculaId, aoFechar, aoSalvar }: { g: GestaoDaMatricula; matriculaId: string; aoFechar: () => void; aoSalvar: (r: { mudancas: MudancaDaGestao[] }) => void }) {
  const { ler, mudar } = useApi();
  const acao = useAcao();
  const salvo = doSalvo(g);
  const [f, setF] = useState<Formulario>(salvo);
  const muda = (x: Partial<Formulario>) => setF((v) => ({ ...v, ...x }));
  const e = g.modulos.etapas;
  const liberado = Boolean(e?.etapa);

  // ---- plano: de outra familia, a etapa tem de ser escolhida na familia nova
  const mudouPlano = f.plano !== salvo.plano;
  const familiaDoNovo = useQuery({ queryKey: ["familia", f.plano], queryFn: () => ler<FamiliaDoPlano>(`/v1/admin/etapas/planos/${f.plano}`), enabled: Boolean(e && liberado && mudouPlano) });
  const familiaAtual = e?.familia?.codigo ?? null;
  const familiaNova = mudouPlano ? (familiaDoNovo.data?.familia ?? null) : familiaAtual;
  const trocouFamilia = Boolean(mudouPlano && familiaDoNovo.data && familiaNova && familiaNova !== familiaAtual);
  const semFamilia = Boolean(mudouPlano && familiaDoNovo.data && !familiaDoNovo.data.familia);
  const etapasDisponiveis = trocouFamilia && familiaNova ? (e?.etapas_por_familia[familiaNova] ?? []) : (e?.etapas_da_familia ?? []);
  useEffect(() => {
    // Trocou de familia: a etapa de antes nao vale (muda de nome e de ordem), entao a escolha comeca vazia.
    if (trocouFamilia) muda({ etapa: "" });
    else if (!mudouPlano) muda({ etapa: salvo.etapa });
  }, [trocouFamilia, familiaNova]);

  // ---- etapa
  // Trocando de familia sem etapa escolhida ainda, nao ha etapa alvo: a mesa espera a escolha.
  const alvo = (trocouFamilia ? f.etapa : f.etapa || salvo.etapa) as Etapa | "";
  const mudouEtapa = trocouFamilia || (f.etapa !== "" && f.etapa !== salvo.etapa);
  // O caminho ate a etapa escolhida, na ordem da familia: e o que o plano "ja percorreu" se ficar nela.
  const caminho = trocouFamilia || !alvo ? [] : etapasDisponiveis.slice(0, etapasDisponiveis.findIndex((x) => x.codigo === alvo) + 1).map((x) => x.codigo);
  const inicioSalvo = (x: Etapa) => e?.inicios.find((i) => i.etapa === x)?.dia ?? "";
  useEffect(() => {
    // Mudou a etapa: o inicio dela comeca no que ja estava salvo (voltando pra uma etapa percorrida) ou em hoje.
    if (mudouEtapa && f.etapa) muda({ dia: inicioSalvo(f.etapa) || hojeEmSaoPaulo() });
  }, [f.etapa]);
  const iniciosEditaveis = caminho.filter((x) => !(mudouEtapa && x === alvo));
  const iniciosMudados = iniciosEditaveis.filter((x) => (f.inicios[x] ?? "") !== inicioSalvo(x));

  // ---- conta: mesa da etapa em que o plano vai ficar, e subconta junto
  const mesas = alvo ? (e?.mesas_por_etapa[alvo] ?? []) : [];
  const mesaSalvaServe = !salvo.mesa || mesas.some((m) => m.conta === salvo.mesa);
  const precisaDeMesaNova = Boolean(mudouEtapa && salvo.mesa && !mesaSalvaServe);
  useEffect(() => {
    // De volta na etapa salva, a mesa salva volta. Etapa nova cuja mesa nao serve: esvazia e pede outra, em vez de
    // mandar uma que o backend recusaria.
    if (alvo === salvo.etapa && !trocouFamilia) muda({ mesa: salvo.mesa });
    else if (!mesas.some((m) => m.conta === f.mesa)) muda({ mesa: mesaSalvaServe ? salvo.mesa : "" });
  }, [alvo, trocouFamilia]);
  const mudouMesa = f.mesa !== salvo.mesa;
  const mudouSubconta = f.subconta.trim() !== salvo.subconta;
  const mexeuNaConta = mudouMesa || mudouSubconta;
  const contaCompleta = Boolean(f.mesa && f.subconta.trim());
  const nomeDaMesa = (conta: string) => {
    const todas = Object.values(e?.mesas_por_etapa ?? {}).flat();
    const m = todas.find((x) => x.conta === conta);
    return m ? `${m.nome} (${m.conta})` : conta;
  };

  // ---- plataforma
  const mudouPlataforma = f.plataforma !== salvo.plataforma;
  const rotuloDaPlataforma = (p: string) => e?.plataformas.find((x) => x.codigo === p)?.rotulo ?? "não informada";

  // Trocando a etapa, a conta vai junto (a subconta de agora vem preenchida e pode ficar a mesma): o backend confere o par
  // na mesa da etapa nova e a tela mostra antes o que vai gravar.
  const temConta = Boolean(salvo.mesa || salvo.subconta);
  const mandaConta = contaCompleta && (mexeuNaConta || mudouEtapa);

  const n = [mudouPlano, mudouEtapa, mudouMesa, mudouSubconta, mudouPlataforma].filter(Boolean).length + iniciosMudados.length;
  const falta =
    mudouEtapa && !f.etapa ? "Escolha a etapa."
    : precisaDeMesaNova && !f.mesa ? `Escolha a mesa de ${ETAPA[alvo as Etapa]}.`
    : (mexeuNaConta || (mudouEtapa && temConta)) && !contaCompleta ? "Informe a mesa e a subconta juntas."
    : n > 0 && f.motivo.trim().length < 5 ? "Escreva o motivo."
    : null;

  // ---- primeira mensalidade: so depois que o backend pede (409 DECIDIR_MENSALIDADE)
  const [pedido, setPedido] = useState<string | null>(null);
  const [mens, setMens] = useState<Mensalidade>({ decisao: "", como: "", dia: hojeEmSaoPaulo(), vencimento: hojeEmSaoPaulo(), motivo: "" });
  const mudaMens = (x: Partial<Mensalidade>) => setMens((v) => ({ ...v, ...x }));
  const faltaNaMensalidade =
    !pedido ? null
    : !mens.decisao ? "Escolha o que acontece com a primeira mensalidade."
    : mens.decisao === "JA_PAGOU" && mens.como.trim().length < 5 ? "Diga como a primeira mensalidade foi paga."
    : mens.decisao === "JA_PAGOU" && !mens.dia ? "Informe o dia do pagamento."
    : mens.decisao === "COBRAR" && !mens.vencimento ? "Informe o vencimento da primeira mensalidade."
    : mens.decisao === "SEM_COBRANCA" && mens.motivo.trim().length < 5 ? "Diga por que fica sem cobrança."
    : null;
  const decisao =
    mens.decisao === "JA_PAGOU" ? { decisao: mens.decisao, como: mens.como.trim(), dia: mens.dia }
    : mens.decisao === "COBRAR" ? { decisao: mens.decisao, vencimento: mens.vencimento }
    : mens.decisao === "SEM_COBRANCA" ? { decisao: mens.decisao, motivo: mens.motivo.trim() }
    : null;

  const erro = useRef<HTMLDivElement>(null);
  const blocoDaMensalidade = useRef<HTMLDivElement>(null);
  useEffect(() => { if (acao.erro) erro.current?.scrollIntoView({ block: "nearest", behavior: "smooth" }); }, [acao.erro]);
  useEffect(() => { if (pedido) blocoDaMensalidade.current?.scrollIntoView({ block: "start", behavior: "smooth" }); }, [pedido]);
  const faltaTudo = falta ?? faltaNaMensalidade;
  const podeSalvar = n > 0 && !faltaTudo;

  // ---- conferencia da conta e pergunta da conta em uso
  const conta = { subconta: f.subconta.trim(), mesa: f.mesa };
  const emUso = useContaEmUso(parDaConta(conta));
  const chaveDaConferencia = mandaConta ? `${alvo}|${parDaConta(conta)}` : null;
  const [conferida, setConferida] = useState<string | null>(null);
  const [conferindo, setConferindo] = useState(false);
  // A pergunta da conta em uso mora na conferencia: se ela chegar com a conferencia fechada, abre.
  useEffect(() => { if (emUso.frase) setConferindo(true); }, [emUso.frase]);

  const enviar = async (confirmarEmUso = false) => {
    let r: { mudancas: MudancaDaGestao[] } | undefined;
    const corpo = {
      motivo: f.motivo.trim(),
      origem: f.origem,
      ...(mudouPlano ? { plano_slug: f.plano } : {}),
      ...(mudouEtapa ? { etapa: f.etapa, dia_da_etapa: f.dia } : {}),
      ...(iniciosMudados.length ? { inicios: Object.fromEntries(iniciosMudados.map((x) => [x, f.inicios[x] ?? ""])) } : {}),
      // Mesa e subconta sempre juntas quando a conta muda ou a etapa muda (o backend confere o par na etapa nova).
      ...(mandaConta || precisaDeMesaNova ? { mesa: f.mesa, subconta: f.subconta.trim() } : {}),
      ...(mudouPlataforma && f.plataforma ? { plataforma: f.plataforma } : {}),
      ...(pedido && decisao ? { mensalidade: decisao } : {}),
    };
    const pedidoDaGestao = async (extra: CorpoDaConfirmacao) => {
      try {
        r = await mudar<{ mudancas: MudancaDaGestao[] }>(`/v1/admin/matriculas/${matriculaId}/gestao`, { ...corpo, ...extra });
      } catch (err) {
        // Nao e erro: o backend precisa saber o que fazer com a 1a mensalidade. Abre o bloco com a frase dele (a conta ja
        // conferida e confirmada vale pro proximo envio).
        if (err instanceof ErroApi && err.status === 409 && err.corpo?.codigo === PEDE_DECISAO) { setConferindo(false); setPedido(err.corpo.erro ?? ""); return; }
        throw err;
      }
    };
    const gravou = await (confirmarEmUso ? emUso.confirmarEReenviar(acao.rodar, pedidoDaGestao) : emUso.enviar(acao.rodar, pedidoDaGestao));
    if (!gravou || !r) return;
    setConferindo(false);
    aoFechar();
    aoSalvar({ mudancas: r.mudancas });
  };
  const salvar = (ev: FormEvent) => {
    ev.preventDefault();
    if (!podeSalvar) return;
    // Mexeu na conta ou na etapa e ainda nao conferiu este par: confere antes de gravar.
    // Com a pergunta da conta em uso ainda sem resposta, volta pra ela.
    if (chaveDaConferencia && (conferida !== chaveDaConferencia || emUso.frase)) { acao.limpar(); setConferindo(true); return; }
    void enviar();
  };
  const conferiuE = (confirmarEmUso: boolean) => { setConferida(chaveDaConferencia); void enviar(confirmarEmUso); };

  const planos = g.planos.some((p) => p.slug === g.plano.slug) ? g.planos : [g.plano, ...g.planos];
  const nomeDoPlano = (slug: string) => planos.find((p) => p.slug === slug)?.nome ?? slug;
  const numero = (x: Etapa) => etapasDisponiveis.findIndex((y) => y.codigo === x) + 1;

  return (
    <>
    <form className="mtc-formulario" onSubmit={salvar} noValidate>
      {acao.erro && <div ref={erro}><Aviso tipo="erro">{acao.erro}</Aviso></div>}

      <BlocoDeFormulario titulo="Plano" descricao={<>Status: <strong>{STATUS_DA_MATRICULA[g.status].rotulo}</strong>. Suspender, retomar e encerrar continuam nos botões da ficha.</>}>
        <CampoMudado mudou={mudouPlano} antes={g.plano.nome}>
          <CampoSelecao rotulo="Plano" valor={f.plano} aoMudar={(v) => muda({ plano: v })} opcoes={planos.map((p) => ({ valor: p.slug, rotulo: p.nome }))} />
        </CampoMudado>
        {trocouFamilia && familiaNova && <Aviso tipo="alerta">O {nomeDoPlano(f.plano)} é da família {FAMILIA[familiaNova]}, e o plano está na {e?.familia?.rotulo}. Escolha a etapa na família nova.</Aviso>}
        {semFamilia && <Aviso tipo="alerta">O {nomeDoPlano(f.plano)} não tem família cadastrada. Defina em Cadastros, Planos, antes de trocar.</Aviso>}
        {e && !liberado && <p className="mtc-bloco__descricao">Este plano ainda não foi liberado: a etapa, a conta e a plataforma nascem na liberação. Antes disso, a plataforma e a data de início mudam pelos botões da ficha.</p>}
      </BlocoDeFormulario>

      {e && liberado && (
        <>
          <BlocoDeFormulario titulo="Etapa">
            <CampoMudado mudou={mudouEtapa} antes={e.etapa?.rotulo}>
              <CampoSelecao<Etapa> rotulo="Etapa" valor={f.etapa} aoMudar={(v) => muda({ etapa: v })} vazio={trocouFamilia ? "Escolha a etapa" : undefined} required opcoes={etapasDisponiveis.map((x, i) => ({ valor: x.codigo, rotulo: `${i + 1}. ${x.rotulo}` }))} />
            </CampoMudado>
            {/* O inicio de cada etapa do caminho, na ordem; o da etapa escolhida (quando muda) e o dia em que ela comeca. */}
            {(mudouEtapa && f.etapa && !caminho.includes(f.etapa) ? [...iniciosEditaveis, f.etapa] : caminho).map((x) =>
              mudouEtapa && x === f.etapa ? (
                <CampoMudado key={x} mudou antes={inicioSalvo(x) ? dia(inicioSalvo(x)) : "não passou por ela"}>
                  <CampoData rotulo={`Início da etapa ${numero(x)}`} valor={f.dia} aoMudar={(v) => muda({ dia: v })} dica={`${ETAPA[x]}, a etapa escolhida: o dia em que o plano entrou nela.`} />
                </CampoMudado>
              ) : (
                <CampoMudado key={x} mudou={iniciosMudados.includes(x)} antes={dia(inicioSalvo(x))}>
                  <CampoData rotulo={`Início da etapa ${numero(x)}`} valor={f.inicios[x] ?? ""} aoMudar={(v) => muda({ inicios: { ...f.inicios, [x]: v } })} dica={inicioSalvo(x) ? ETAPA[x] : `${ETAPA[x]}. Ainda não informado.`} />
                </CampoMudado>
              ),
            )}
          </BlocoDeFormulario>

          <BlocoDeFormulario titulo="Conta" descricao={precisaDeMesaNova ? undefined : "Mesa e subconta andam juntas. Isto corrige o que está guardado aqui: a conta na extranet da Nelogica não muda."}>
            {precisaDeMesaNova && <Aviso tipo="alerta">A conta está na mesa {nomeDaMesa(salvo.mesa)}, que não é de {ETAPA[alvo as Etapa]}. Escolha a mesa e confira a subconta de {ETAPA[alvo as Etapa]}.</Aviso>}
            <CampoMudado mudou={mudouMesa} antes={salvo.mesa ? nomeDaMesa(salvo.mesa) : "sem mesa"}>
              <CampoSelecao rotulo="Mesa" valor={f.mesa} aoMudar={(v) => muda({ mesa: v })} vazio={alvo ? "Escolha a mesa" : "Escolha a etapa antes"} required opcoes={mesas.map((m) => ({ valor: m.conta, rotulo: `${m.conta}, ${m.nome}` }))} disabled={!alvo} />
            </CampoMudado>
            <CampoMudado mudou={mudouSubconta} antes={salvo.subconta || "sem subconta"}>
              <Campo rotulo="Subconta" valor={f.subconta} aoMudar={(v) => muda({ subconta: soDigitos(v).slice(0, 12) })} inputMode="numeric" mono dica="Só números, como está na extranet." />
            </CampoMudado>
          </BlocoDeFormulario>

          <BlocoDeFormulario titulo="Plataforma">
            <CampoMudado mudou={mudouPlataforma} antes={rotuloDaPlataforma(salvo.plataforma)}>
              <Segmentado<Plataforma> rotulo="Plataforma" nome={`plataforma-${matriculaId}`} valor={f.plataforma as Plataforma} aoMudar={(v) => muda({ plataforma: v })} opcoes={e.plataformas.map((p) => ({ valor: p.codigo, rotulo: p.rotulo }))} />
            </CampoMudado>
          </BlocoDeFormulario>
        </>
      )}

      {pedido !== null && (
        <div ref={blocoDaMensalidade}>
          <BlocoDeFormulario titulo="Primeira mensalidade">
            <Aviso tipo="alerta">{pedido}</Aviso>
            <ListaDeEscolha
              rotulo="O que acontece com a primeira"
              nome={`mensalidade-${matriculaId}`}
              valor={mens.decisao}
              aoMudar={(v) => mudaMens({ decisao: v as DecisaoDaMensalidade })}
              opcoes={[
                { valor: "JA_PAGOU", titulo: "Já foi paga", detalhe: "O cliente pagou por fora. A primeira entra como paga, com o como e o dia." },
                { valor: "COBRAR", titulo: "Cobrar", detalhe: "A primeira fica a pagar na área do cliente, no vencimento que você escolher." },
                { valor: "SEM_COBRANCA", titulo: "Sem cobrança neste mês", detalhe: "Não cobra a deste mês. A primeira passa a vencer na próxima data do ciclo e a equipe é avisada no sino." },
              ]}
            />
            {mens.decisao === "JA_PAGOU" && (
              <>
                <CampoTextoLongo rotulo="Como foi paga" valor={mens.como} aoMudar={(v) => mudaMens({ como: v })} maxLength={500} placeholder="Ex.: PIX pelo WhatsApp, conferido." dica="Pelo menos 5 letras. Fica na mensalidade e na trilha." />
                <CampoData rotulo="Dia do pagamento" valor={mens.dia} aoMudar={(v) => mudaMens({ dia: v })} max={hojeEmSaoPaulo()} dica="Hoje ou antes." />
              </>
            )}
            {mens.decisao === "COBRAR" && (
              <CampoData rotulo="Vencimento da primeira" valor={mens.vencimento} aoMudar={(v) => mudaMens({ vencimento: v })} min={hojeEmSaoPaulo()} dica="Hoje ou depois." />
            )}
            {mens.decisao === "SEM_COBRANCA" && (
              <CampoTextoLongo rotulo="Por que fica sem cobrança" valor={mens.motivo} aoMudar={(v) => mudaMens({ motivo: v })} maxLength={500} placeholder="Ex.: bonificação combinada com o cliente." dica="Pelo menos 5 letras. Vai no aviso da equipe e na trilha." />
            )}
          </BlocoDeFormulario>
        </div>
      )}

      <BlocoDeFormulario titulo="Por que">
        <CampoTextoLongo rotulo="Motivo" valor={f.motivo} aoMudar={(v) => muda({ motivo: v })} maxLength={500} placeholder="Ex.: cadastrado errado na liberação, conferido na extranet." dica="Pelo menos 5 letras. Fica na trilha, com o seu nome; o cliente não lê." />
        <CampoSelecao rotulo="De onde veio o pedido" valor={f.origem} aoMudar={(v) => muda({ origem: v })} required opcoes={ORIGENS_DA_MUDANCA} />
      </BlocoDeFormulario>

      <div className="mtc-dialogo__rodape mtc-dialogo__rodape--fixo">
        {faltaTudo && n > 0 && <span className="mtc-texto-2" style={{ flex: "1 1 100%", fontSize: "var(--t-sm)" }} aria-live="polite">{faltaTudo}</span>}
        <Botao variante="fantasma" onClick={aoFechar}>Cancelar</Botao>
        <Botao type="submit" carregando={acao.carregando && !conferindo} disabled={!podeSalvar}>{n ? `Salvar ${plural(n)}` : "Nada mudou ainda"}</Botao>
      </div>
    </form>
    {alvo && (
      <ConfirmarConta
        aberto={conferindo}
        aoFechar={() => setConferindo(false)}
        aoConfirmar={() => conferiuE(false)}
        emUso={emUso.frase}
        aoConfirmarEmUso={() => conferiuE(true)}
        carregando={acao.carregando}
        erro={acao.erro}
        titulo="Confere antes de salvar"
        botao="Confirmar e salvar"
        etapa={alvo}
        conta={conta}
        mesas={mesas}
        antes={{ subconta: salvo.subconta || null, mesa: salvo.mesa || null, etapa: e?.etapa?.rotulo ?? null }}
        extra={[
          ...(mudouEtapa ? [{ rotulo: `Início da etapa ${numero(alvo)}`, valor: dia(f.dia) }] : []),
          ...(mudouPlano ? [{ rotulo: "Plano", valor: nomeDoPlano(f.plano), detalhe: `Antes: ${g.plano.nome}` }] : []),
          ...(mudouPlataforma ? [{ rotulo: "Plataforma", valor: rotuloDaPlataforma(f.plataforma), detalhe: `Antes: ${rotuloDaPlataforma(salvo.plataforma)}` }] : []),
        ]}
      />
    )}
    </>
  );
}
